import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Empty, Segmented, Space, Typography, Row, Col, Statistic } from "antd";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { Card, Skeleton, PageHeader } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";

const { Text } = Typography;

/** Daily token consumption for the signed-in client. */
const PortalUsage = () => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [days, setDays] = useState(30);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overdraft, setOverdraft] = useState(null);

  // Usage is the page a client opens when consumption looks wrong or calls have stopped, so the
  // outstanding balance is repeated here rather than living only on the subscription page.
  useEffect(() => {
    let cancelled = false;
    mutateRef.current("portalUsageOverdraft", {
      method: "get",
      endpoint: "/portal/subscription",
      onSuccess: (data) => {
        if (!cancelled) setOverdraft(data?.overdraft ?? null);
      },
      onError: () => {},
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async (windowDays) => {
    setLoading(true);
    await mutateRef.current("portalUsage", {
      method: "get",
      endpoint: "/portal/usage",
      params: { days: windowDays },
      onSuccess: (data) => setSeries(data?.points || []),
      onError: () => setSeries([]),
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const total = series.reduce((sum, p) => sum + (p.tokens || 0), 0);
  const busiest = series.reduce(
    (best, p) => ((p.tokens || 0) > (best?.tokens || 0) ? p : best),
    null
  );

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <PageHeader
        title="Usage"
        subtitle="Token consumption across your agents."
        className="page-header--flush"
      />

      {overdraft?.outstanding > 0 && (
        <Alert
          type={overdraft.isOverdue ? "error" : "warning"}
          showIcon
          message={
            overdraft.isOverdue
              ? `Requests are paused — ${Number(overdraft.outstanding).toLocaleString()} tokens to settle`
              : `${Number(overdraft.outstanding).toLocaleString()} tokens used on account`
          }
          description={`Settle by ${dayjs(overdraft.dueAtUtc).format(
            "DD MMM YYYY"
          )}. A top-up clears this balance first; anything left over becomes usable credit.`}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title={`Tokens (last ${days} days)`} value={total} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Busiest day"
              value={busiest?.tokens ? dayjs(busiest.date).format("DD MMM YYYY") : "—"}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Daily average"
              value={series.length ? Math.round(total / series.length) : 0}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Token consumption"
        extra={
          <Segmented
            size="small"
            value={days}
            onChange={setDays}
            options={[
              { label: "7d", value: 7 },
              { label: "30d", value: 30 },
              { label: "90d", value: 90 },
            ]}
          />
        }
        padding="md"
      >
        {loading ? (
          <Skeleton rows={4} />
        ) : series.some((p) => p.tokens > 0) ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={series.map((p) => ({
                date: dayjs(p.date).format("DD MMM"),
                tokens: p.tokens,
                cumulative: p.cumulativeTokens,
              }))}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="portalUsageFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent-blue)" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="var(--accent-blue)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                width={56}
                tickFormatter={(v) => v.toLocaleString()}
              />
              <RechartsTooltip
                formatter={(v, name) => [
                  Number(v).toLocaleString(),
                  name === "tokens" ? "Daily" : "Cumulative",
                ]}
                contentStyle={{
                  background: "var(--surface-raised, #1a1d24)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="tokens"
                stroke="var(--accent-blue)"
                strokeWidth={2}
                fill="url(#portalUsageFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Empty description="No consumption recorded in this window" />
        )}
      </Card>

      <Text type="secondary" style={{ fontSize: 12 }}>
        Usage is recorded when a request completes successfully. Calls made with a
        test-environment key are not billed.
      </Text>
    </Space>
  );
};

export default PortalUsage;
